import { useState, useEffect, useRef } from 'react';
import { DocumentCard, Document, DocumentType } from '@/components/DocumentCard';

interface BuyFlowStep1Props {
  productId: string;
  onStateChange: (data: { allDocumentsApproved: boolean; approvedCount: number }) => void;
}

/**
 * Buy Flow Step 1: Review Documents
 * 
 * Displays list of required documents for review and approval.
 * Features:
 * - 5 required documents with correct names matching epic
 * - DocumentCard component integration (clickable rows with inline expansion)
 * - Inline document expansion showing document preview
 * - Approval/disapproval workflow with state management
 * - Real-time approval count tracking
 * - Accessibility features (ARIA labels, keyboard navigation, screen reader announcements)
 * - Responsive layout (desktop ≥1024px, tablet 768-1023px, mobile <768px)
 * 
 * User Interactions:
 * - Click document row → Expands inline to show document preview
 * - Click Approve (in expanded view) → Changes status to "approved"
 * - Click Disapprove (in expanded view) → Reverts status to "pending"
 */
export const BuyFlowStep1 = ({ productId, onStateChange }: BuyFlowStep1Props) => {
  // Track which document is expanded (by ID)
  const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null);

  // Create mock documents with all 5 required document types
  // All documents start with "pending" status
  const createMockDocuments = (productId: string): Document[] => {
    const baseDate = new Date();
    const documentTypes: DocumentType[] = [
      'allocation_declaration',
      'inspection_certificate',
      'warehouse_receipt',
      'certificate_of_origin',
      'other',
    ];

    return documentTypes.map((type, index) => ({
      id: `doc-${index + 1}-${productId}`,
      ownerUserId: 'user-1',
      productId: productId,
      type: type,
      fileName: `${type.replace(/_/g, '-')}.pdf`,
      url: '/images/products/oil_barrelss.jpg', // Placeholder image for mock PDF preview
      status: 'pending' as const,
      uploadedAt: new Date(baseDate.getTime() - (7 - index) * 24 * 60 * 60 * 1000),
    }));
  };

  const [documents, setDocuments] = useState<Document[]>(() => createMockDocuments(productId));

  // Calculate approval status
  const approvedCount = documents.filter(doc => doc.status === 'approved').length;
  const allDocumentsApproved = approvedCount === documents.length;

  // Track previous values to avoid unnecessary updates
  const prevValuesRef = useRef({ allDocumentsApproved, approvedCount });

  // Update parent state when approval status changes
  useEffect(() => {
    // Only update if values actually changed
    if (
      prevValuesRef.current.allDocumentsApproved !== allDocumentsApproved ||
      prevValuesRef.current.approvedCount !== approvedCount
    ) {
      prevValuesRef.current = { allDocumentsApproved, approvedCount };
      onStateChange({ allDocumentsApproved, approvedCount });
    }
  }, [allDocumentsApproved, approvedCount, onStateChange]);


  // Handle document approval
  const handleApprove = (documentId: string) => {
    const updatedDocuments = documents.map(doc =>
      doc.id === documentId
        ? { ...doc, status: 'approved' as const, approvedAt: new Date() }
        : doc
    );
    setDocuments(updatedDocuments);

    const newApprovedCount = updatedDocuments.filter(doc => doc.status === 'approved').length;
    const newAllApproved = newApprovedCount === documents.length;
    onStateChange({ allDocumentsApproved: newAllApproved, approvedCount: newApprovedCount });

    // Screen reader announcement for approval status change
    const approvedDoc = updatedDocuments.find(doc => doc.id === documentId);
    if (approvedDoc) {
      const docTypeNames: Record<DocumentType, string> = {
        allocation_declaration: 'Allocation declaration',
        inspection_certificate: 'Inspection certificate',
        warehouse_receipt: 'Warehouse receipt(s)',
        certificate_of_origin: 'Certificate of origin',
        other: 'Other',
      };
      // Announce to screen readers
      const announcement = `${docTypeNames[approvedDoc.type]} approved. ${newApprovedCount} of ${documents.length} documents approved.`;
      const liveRegion = document.getElementById('screen-reader-announcements');
      if (liveRegion) {
        liveRegion.textContent = announcement;
      }
    }
  };

  // Handle document disapproval
  const handleDisapprove = (documentId: string) => {
    const updatedDocuments = documents.map(doc =>
      doc.id === documentId
        ? { ...doc, status: 'pending' as const, approvedAt: undefined }
        : doc
    );
    setDocuments(updatedDocuments);

    const newApprovedCount = updatedDocuments.filter(doc => doc.status === 'approved').length;
    const newAllApproved = newApprovedCount === documents.length;
    onStateChange({ allDocumentsApproved: newAllApproved, approvedCount: newApprovedCount });

    // Screen reader announcement for disapproval status change
    const disapprovedDoc = updatedDocuments.find(doc => doc.id === documentId);
    if (disapprovedDoc) {
      const docTypeNames: Record<DocumentType, string> = {
        allocation_declaration: 'Allocation declaration',
        inspection_certificate: 'Inspection certificate',
        warehouse_receipt: 'Warehouse receipt(s)',
        certificate_of_origin: 'Certificate of origin',
        other: 'Other',
      };
      // Announce to screen readers
      const announcement = `${docTypeNames[disapprovedDoc.type]} disapproved. ${newApprovedCount} of ${documents.length} documents approved.`;
      const liveRegion = document.getElementById('screen-reader-announcements');
      if (liveRegion) {
        liveRegion.textContent = announcement;
      }
    }
  };

  // Handle document card click (toggle expansion)
  const handleDocumentClick = (documentId: string) => {
    setExpandedDocumentId(expandedDocumentId === documentId ? null : documentId);
  };

  return (
    <div className="w-full">
      {/* Screen reader announcements region */}
      <div
        id="screen-reader-announcements"
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />

      {/* Title */}
      <h1 className="text-3xl font-bold text-text-primary mb-2">Review Documents</h1>

      {/* Instruction */}
      <p className="text-base text-text-secondary mb-8">
        Please review and approve all required documents
      </p>

      {/* Document List */}
      <div className="space-y-3 overflow-y-auto" role="list" aria-label="Required documents for review">
        {documents.map((document) => (
          <div key={document.id} role="listitem">
            <DocumentCard
              document={document}
              isExpanded={expandedDocumentId === document.id}
              onApprove={
                document.status === 'pending'
                  ? () => handleApprove(document.id)
                  : undefined
              }
              onDisapprove={
                document.status === 'approved'
                  ? () => handleDisapprove(document.id)
                  : undefined
              }
              onClick={() => handleDocumentClick(document.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

